import classNames from 'classnames';
import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import { useState } from 'react';
import Button from '../../components/Button';
import { DiscussionSkeleton } from '../../components/Entities/Discussion/DiscussionCard';
import { RoomSkeleton } from '../../components/Entities/RoomCard';
import { getLayout } from '../../components/Layouts/DiscoverLayout/DiscoverLayout';
import { useEntity } from '../../lib/entity';

const DiscussionCard = dynamic(
  () => import('../../components/Entities/Discussion/DiscussionCard'),
  {
    loading: DiscussionSkeleton,
  },
);

const RoomCard = dynamic(() => import('../../components/Entities/RoomCard'), {
  loading: RoomSkeleton,
});

const types = {
  ROOMS: 'rooms',
  DISCUSSIONS: 'discussions',
};

function Discover() {
  const [section, setSection] = useState(types.ROOMS);

  const activeDiscussionId = useEntity().activeDiscussionId;
  const entityRooms = useEntity().rooms;
  const discussions = useEntity().discussions;
  const isLoading = useEntity().state.isLoading;

  const getDiscussions = () => {
    if (activeDiscussionId) {
      return discussions.filter((d) => d.id !== activeDiscussionId && !d.ended);
    }
    return discussions.filter((d) => !d.ended);
  };

  const allDiscussions = getDiscussions();

  const tabsClassNames = classNames('tabs');

  const hasNoRooms = entityRooms.length < 1;
  const hasNoDiscussions = allDiscussions.length < 1 && !activeDiscussionId;

  const homeSections = {
    discussions: {
      title: 'Live Discussions',
      items: allDiscussions,
    },
    rooms: {
      title: 'Rooms',
      items: entityRooms,
    },
  };

  const homeFeed = {
    all: {
      ...homeSections,
    },
    ...homeSections,
  };

  const handleChange = (newSection) => setSection(newSection);

  return (
    <>
      <NextSeo
        canonical={`${process.env.NEXT_PUBLIC_DOMAIN}discover-the-best-trending-film-and-tv-buffs-live-discussion-2021`}
        title="Discover The Best Trending Film & TV Buffs Live Discussion 2021 - FTVBs"
        description="Discover The Best Trending Film & TV Buffs Live Discussion 2021 - FTVBs. With FTVBs You Can Find & Discover The Best Film and TV Buffs Discussion Community For London UK Audience Or Share Your Opinions By Joining & Creating Rooms On This Page Of FTVBs."
      />
      {isLoading && (
        <section className="feed space-y-4 p-4 md:p-8">
          {[...Array(3).keys()].map((i) => (
            <RoomSkeleton key={i} />
          ))}
        </section>
      )}
      {!isLoading && (
        <>
          <section className={tabsClassNames}>
            <section className="wrapper">
              {!hasNoRooms &&
                !hasNoDiscussions &&
                Object.entries(types).map(([type, newType]) => {
                  const wrapperClasses = classNames('wrapper', {
                    underlined: newType === section,
                  });
                  return (
                    <section key={type} className="tab">
                      <Button
                        variant="clear"
                        className={wrapperClasses}
                        onClick={() => handleChange(newType)}
                      >
                        {homeSections[newType]?.title}
                      </Button>
                    </section>
                  );
                })}
            </section>
          </section>
          {homeSections[section] && (
            <section id="feed" className="feed section">
              <section className="wrapper">
                <section className="item item--full">
                  <h2 className="title">{homeFeed[section].title}</h2>
                </section>
                {homeFeed[section].items.map((item) =>
                  section === types.DISCUSSIONS ? (
                    <DiscussionCard key={item.id} discussion={item} />
                  ) : (
                    <RoomCard key={item.id} room={item} />
                  ),
                )}
                {!homeFeed[section].items.length && (
                  <section className="w-full text-center p-4 space-y-4">
                    <h3 className="text-xl text-gray-400">
                      {section === types.DISCUSSIONS ? '🎙' : '🚪'}
                      <br />
                      No {`${activeDiscussionId && section === types.DISCUSSIONS ? 'other ' : ''}`}
                      {section} right now
                    </h3>
                  </section>
                )}
              </section>
            </section>
          )}
        </>
      )}
    </>
  );
}

export const getStaticProps = async () => {
  return {
    props: {
      analytics: {
        category: 'Discover',
        name: 'Discover',
      },
    },
  };
};

Discover.getLayout = getLayout;

export default Discover;
